import { useNavigate } from 'react-router';
import { useImageUrl } from '../db/useImageUrl';
import { iconByName } from '../rooms/roomIcons';
import { EmptyState } from '../ui/EmptyState';
import { HighlightMatch } from './Highlight';
import { snippetOf, textOf, type Field } from './match';
import type { Hit, Results } from './useResults';

/**
 * Everything, grouped by room. The room is shown because it's the answer to
 * the question the search was really asking — you know you bought the thing,
 * you just can't remember which board you put it on.
 */
export function SearchResults({
  results,
  query,
  onPick,
}: {
  results: Results;
  query: string;
  onPick: () => void;
}) {
  if (query.trim() === '') {
    return (
      <p className="search__hint">
        Search names, descriptions, your notes, and where a thing came from.
      </p>
    );
  }

  if (!results.ready) return <div className="search__results" aria-busy="true" />;

  if (results.count === 0) {
    return (
      <div className="search__empty">
        <EmptyState title="Nothing Matches" line={`No item mentions “${query.trim()}”.`} />
      </div>
    );
  }

  return (
    <div className="search__results">
      {results.groups.map((group) => {
        const Icon = iconByName(group.room.icon);
        return (
          <section key={group.room.id} className="search__group">
            <h3 className="section-label search__room">
              <Icon size={14} aria-hidden />
              {group.room.name}
            </h3>
            <ul className="search__list">
              {group.hits.map((hit) => (
                <Row key={hit.item.id} hit={hit} roomId={group.room.id} onPick={onPick} />
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function Row({ hit, roomId, onPick }: { hit: Hit; roomId: string; onPick: () => void }) {
  const navigate = useNavigate();
  const url = useImageUrl(hit.item.coverImageId);
  const { match, item } = hit;

  return (
    <li>
      <button
        type="button"
        className="search__hit"
        onClick={() => {
          onPick();
          // Straight to the panel, inside the room it lives in. A section in
          // between isn't in the address — the panel reads its own record.
          void navigate(`/room/${roomId}/${item.id}`);
        }}
      >
        <span className="search__thumb">
          {url !== undefined && <img src={url} alt="" draggable={false} />}
        </span>

        <span className="search__text">
          <span className="search__title">
            {match.field === 'title' ? (
              <HighlightMatch text={item.title} match={match} />
            ) : (
              item.title
            )}
          </span>

          {match.field !== 'title' && <Context hit={hit} />}
        </span>
      </button>
    </li>
  );
}

/**
 * Where the match actually was, quoted. Notes are called out by name: a hit in
 * something you wrote yourself reads completely differently from one in copy
 * the shop wrote, and it changes what the result means.
 */
function Context({ hit }: { hit: Hit }) {
  const snippet = snippetOf(textOf(hit.item, hit.match.field), hit.match);
  const label = LABELS[hit.match.field];

  return (
    <span className="search__context">
      {label !== undefined && <span className="search__where">{label}</span>}
      {snippet.before}
      <mark className="hit">{snippet.hit}</mark>
      {snippet.after}
    </span>
  );
}

const LABELS: Partial<Record<Field, string>> = {
  notes: 'matched in your notes',
  source: 'matched in the link',
};
